import { useMemo } from 'react';

import { ConsolidatedForecastDetail } from '@futrli-sdk/consolidations';
import { Forecast } from '@futrli-sdk/predict';

import { ConsolidatedForecastReport, VIEW_TYPES } from '../../../../../../core/types';
import { convertForecastLayoutToGridRows } from './utils';
import { FRForecastLayoutItem, ForecastTree } from '../../../types';
import { REPORT_SECTION } from '../../../../../../core/constants';
import { usePredictionsInfoContext } from '../../../../context/predictions-info-context';
import { useForecastColumns } from '../../useForecastColumns/useForecastColumns';
import { useProcessedForecastTrees } from '../../useProcessedForecastTrees/useProcessedForecastTrees';

export const useFRForecastGrid = (
  forecast: Forecast | ConsolidatedForecastDetail | null | undefined,
  forecastReport: ConsolidatedForecastReport | null | undefined,
  accountNameMap: Record<string, string> = {},
  layoutFunc: (trees: Record<string, ForecastTree>) => FRForecastLayoutItem[]
) => {
  const { columns, columnsWithFYInfo } = useForecastColumns(forecastReport);
  const { viewType, thisNumberExplained } = usePredictionsInfoContext();

  const section = viewType === VIEW_TYPES.CASH_FLOW ? REPORT_SECTION.CashFlow : REPORT_SECTION.ProfitLoss;

  // We get a few different trees from the backend, balance_sheet Profit_and_loss etc.
  // Combine them, process them and then filter out any nodes we dont want to show from these trees
  const filteredTrees = useProcessedForecastTrees(forecast, forecastReport, accountNameMap, thisNumberExplained);

  // Get the forecast layout by passing the filtered tree data to the layout generator function
  const layout = useMemo(() => layoutFunc(filteredTrees), [filteredTrees, layoutFunc]);

  // Convert the layout data to a flat array of grid rows and apply patches
  const { totalRow, rows: dataGridRows } = useMemo(() => {
    return convertForecastLayoutToGridRows(layout, section);
  }, [layout, section]);

  return {
    columns,
    columnsWithFYInfo,
    dataGridRows,
    totalRow
  };
};
